import { Heart, Facebook, Instagram, Mail, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";

const footerLinks = [
  { href: "#accueil", label: "Accueil" },
  { href: "#race", label: "Le Sibérien" },
  { href: "#elevage", label: "Notre Élevage" },
  { href: "#chats", label: "Nos Chats" },
  { href: "#portees", label: "Portées" },
  { href: "#adoption", label: "Adoption" },
  { href: "#conseils", label: "Conseils" },
  { href: "#contact", label: "Contact" },
];

const socialLinks = [
  {
    icon: Facebook,
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=100094255166375",
  },
  {
    icon: Instagram,
    label: "Instagram",
    href: "https://www.instagram.com/la_taiga_siberia",
  },
  {
    icon: Mail,
    label: "Email",
    href: "mailto:lataigasiberia@gmail.com",
  },
];

export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNavClick = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer
      data-testid="footer"
      className="relative bg-forest text-white overflow-hidden"
    >
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-autumn via-sand to-autumn" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">🐱</span>
              <div>
                <h3 className="font-heading text-2xl font-bold">La Taïga Siberia</h3>
                <p className="font-body text-sm text-sand/80">Élevage familial de chats Sibériens</p>
              </div>
            </div>
            <p className="font-body text-cream/70 mb-6 max-w-md">
              Des compagnons d'exception, élevés avec amour et passion dans un cadre 
              familial. Nous partageons notre passion pour cette magnifique race avec 
              des familles qui sauront les chérir.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-autumn rounded-lg flex items-center justify-center transition-colors"
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Navigation</h4>
            <ul className="space-y-2">
              {footerLinks.slice(0, 4).map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(link.href);
                    }}
                    className="font-body text-cream/70 hover:text-autumn transition-colors"
                    data-testid={`footer-link-${link.href.slice(1)}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg font-semibold mb-4">Informations</h4>
            <ul className="space-y-2">
              {footerLinks.slice(4).map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(link.href);
                    }}
                    className="font-body text-cream/70 hover:text-autumn transition-colors"
                    data-testid={`footer-link-${link.href.slice(1)}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-body text-sm text-cream/60 flex items-center gap-1">
            © {new Date().getFullYear()} La Taïga Siberia. Fait avec 
            <Heart className="w-4 h-4 text-autumn inline mx-1" /> 
            pour nos Sibériens.
          </p>
          
          <div className="flex items-center gap-4">
            <a 
              href="#mentions-legales" 
              className="font-body text-xs text-cream/50 hover:text-cream transition-colors"
            >
              Mentions légales
            </a>
            <span className="text-cream/30">|</span>
            <a 
              href="#confidentialite" 
              className="font-body text-xs text-cream/50 hover:text-cream transition-colors"
            >
              Confidentialité
            </a>
          </div>
        </div>
      </div>

      {/* Back to top button */}
      <Button
        onClick={scrollToTop}
        variant="ghost"
        size="icon"
        className="fixed bottom-6 right-6 w-12 h-12 bg-autumn hover:bg-autumn-dark text-white rounded-full shadow-lg z-40 transition-all duration-300 pointer-events-auto"
        data-testid="back-to-top-btn"
        style={{ zIndex: 40 }}
      >
        <ChevronUp className="w-6 h-6" />
      </Button>
    </footer>
  );
}
